/**
 * WatchZonePromotion
 *
 * Contextual prompt that surfaces the Watch Zones feature in places
 * where the user is in the right mindset to set one up. Used in:
 *   - FireDetailPanel (pill variant: "Watch this area for new fires")
 *   - MobileFireDetailPanel (same)
 *   - MobileSidebar empty-state CTA (banner variant)
 *   - First-visit onboarding nudge (banner variant)
 *
 * Visual language matches the bottom-sheet's chip pill design — same
 * 10px radius, severity-coloured accent, app-native typography — so it
 * feels like a first-class part of the panel rather than an ad slot.
 *
 * The dismiss button calls `dismissPromotion(promotionId)` so the user
 * can stop a specific prompt without affecting the other two.
 */

import { memo } from 'react';
import { BellIcon } from './icons';
import { dismissPromotion, type PromotionId } from '../utils/watchZonePromotion';

interface WatchZonePromotionProps {
  promotionId: PromotionId;
  variant?: 'pill' | 'banner';
  /** Headline. Defaults vary by variant. */
  title?: string;
  /** Body copy under the headline. */
  description?: string;
  /** Call-to-action button label. */
  ctaLabel?: string;
  /** Called when the user taps the CTA. */
  onActivate: () => void;
  /** Called when the user dismisses (in addition to the localStorage write). */
  onDismiss?: () => void;
}

function WatchZonePromotionInner({
  promotionId,
  variant = 'pill',
  title,
  description,
  ctaLabel,
  onActivate,
  onDismiss,
}: WatchZonePromotionProps) {
  const handleDismiss = () => {
    dismissPromotion(promotionId);
    onDismiss?.();
  };

  const defaultTitle = variant === 'banner'
    ? 'Be alerted when fires appear near here'
    : 'Watch this area for new fires';
  const defaultDesc = variant === 'banner'
    ? 'Set up a Watch Zone and we will notify you the moment a new fire is reported in this area.'
    : 'Get a notification any time a fire is reported within the area you choose.';
  const defaultCta = 'Set up Watch Zone';

  return (
    <div className={`watch-zone-promo watch-zone-promo-${variant}`} role="region" aria-label="Watch zone promotion">
      <div className="watch-zone-promo-icon" aria-hidden="true">
        <BellIcon size={variant === 'pill' ? 18 : 22} />
      </div>
      <div className="watch-zone-promo-body">
        <div className="watch-zone-promo-title">{title ?? defaultTitle}</div>
        <div className="watch-zone-promo-desc">{description ?? defaultDesc}</div>
      </div>
      <div className="watch-zone-promo-actions">
        <button
          type="button"
          className="watch-zone-promo-cta"
          onClick={onActivate}
        >
          {ctaLabel ?? defaultCta}
        </button>
      </div>
      <button
        type="button"
        className="watch-zone-promo-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss"
        title="Don't show this again"
      >
        &times;
      </button>
    </div>
  );
}

const WatchZonePromotion = memo(WatchZonePromotionInner);
WatchZonePromotion.displayName = 'WatchZonePromotion';

export default WatchZonePromotion;
